import React, { useRef, useEffect, useState, useCallback } from "react";
import { NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import DownloadIcon from "../../../assets/svg/download-icon";
import DuplicateIcon from "../../../assets/svg/duplicate-icon";
import TrashIcon from "../../../assets/svg/trash-icon";

const PORTAL_SELECTORS =
  "[data-image-toolbar-portal], [data-desc-bubble-toolbar], [data-toolbar-portal], .tippy-box";

export interface ResizableImageNodeViewProps extends NodeViewProps {
  imgMaxWidth?: string;
}

export const ResizableImageNodeView: React.FC<ResizableImageNodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  deleteNode,
  editor,
  getPos,
  imgMaxWidth = "100%",
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [showContextMenu, setShowContextMenu] = useState(false);

  useEffect(() => {
    if (!selected) {
      setShowContextMenu(false);
    }
  }, [selected]);

  useEffect(() => {
    if (!imgRef.current) return;
    const img = imgRef.current;
    const setDims = () => {
      if (img.naturalWidth > 0) {
        setOriginalDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
    if (img.complete) setDims();
    else img.onload = setDims;
  }, [node.attrs.src]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!selected || !wrapperRef.current) return;
      if (wrapperRef.current.contains(event.target as Node)) return;
      const portals = document.querySelectorAll(PORTAL_SELECTORS);
      for (const el of portals) {
        if (el.contains(event.target as Node)) return;
      }
      editor
        .chain()
        .focus()
        .setTextSelection(editor.state.doc.content.size)
        .run();
      setShowContextMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selected, editor]);

  const focusAfter = useCallback(() => {
    if (typeof getPos === "function") {
      const pos = getPos();
      if (pos !== undefined) {
        editor
          .chain()
          .focus()
          .setTextSelection(pos + node.nodeSize)
          .run();
        return;
      }
    }
    editor.chain().focus().run();
  }, [editor, getPos, node.nodeSize]);

  const focusBefore = useCallback(() => {
    if (typeof getPos === "function") {
      const pos = getPos();
      if (pos !== undefined) {
        editor.chain().focus().setTextSelection(pos).run();
        return;
      }
    }
    editor.chain().focus().run();
  }, [editor, getPos]);

  const handleResize = (
    e: React.MouseEvent,
    corner: "tr" | "tl" | "br" | "bl"
  ) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = wrapperRef.current?.offsetWidth ?? 0;
    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = corner.includes("r")
        ? startWidth + deltaX
        : startWidth - deltaX;
      updateAttributes({ width: `${Math.max(50, newWidth)}px` });
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      focusAfter();
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const setSize = (width: string | null) => {
    if (width === null && originalDimensions) {
      updateAttributes({ width: `${originalDimensions.width}px` });
    } else {
      updateAttributes({ width: width || "auto" });
    }
    focusAfter();
  };

  const handleSetAlignment = (align: string) => {
    updateAttributes({ textAlign: align });
    focusAfter();
  };

  const handleDownload = useCallback(async () => {
    const src = node.attrs.src;
    if (!src) return;
    setShowContextMenu(false);
    try {
      const fetchResponse = await fetch(src, { cache: "no-store" });
      const blob = await fetchResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const urlFilename = src.split("/").pop()?.split("?")[0];
      link.download = urlFilename || node.attrs.alt || "image";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(src, "_blank");
    }
  }, [node.attrs.src, node.attrs.alt]);

  const handleDuplicate = useCallback(() => {
    if (typeof getPos === "function") {
      const pos = getPos();
      if (pos !== undefined) {
        editor
          .chain()
          .focus()
          .insertContentAt(pos + node.nodeSize, {
            type: "paragraph",
            content: [{ type: "image", attrs: { ...node.attrs } }],
          })
          .run();
      }
    }
    setShowContextMenu(false);
  }, [editor, getPos, node]);

  const handleDelete = useCallback(() => {
    deleteNode();
    setShowContextMenu(false);
  }, [deleteNode]);

  const handleMoreMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowContextMenu((prev) => !prev);
  };

  const currentAlign = node.attrs.textAlign || "left";
  const widthValue = node.attrs.width || "100%";

  const marginLeft =
    node.attrs.indentLevel > 0
      ? `${node.attrs.indentLevel * 1.5}rem`
      : currentAlign === "right"
        ? "auto"
        : undefined;

  const marginRight = currentAlign === "center" ? "auto" : undefined;

  return (
    <NodeViewWrapper
      as="span"
      ref={wrapperRef}
      style={{
        width: widthValue,
        maxWidth: imgMaxWidth,
        display: "inline-block",
        verticalAlign: "top",
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
        marginLeft: currentAlign === "center" ? "auto" : marginLeft,
        marginRight: marginRight || "0.25rem",
        lineHeight: 0,
        position: "relative",
        boxSizing: "border-box",
        border:
          selected && editor.isEditable
            ? "2px solid #3b82f6"
            : "2px solid transparent",
        borderRadius: "6px",
        cursor: "pointer",
      }}
      data-drag-handle
      onClick={(e: React.MouseEvent) => {
        if (!editor.isEditable) return;
        if (e.target === wrapperRef.current || e.target === imgRef.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        className="absolute -top-2 left-0 w-4 h-4 cursor-text z-10"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          focusBefore();
        }}
      />

      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ""}
        draggable={false}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          borderRadius: "4px",
          objectFit: "cover",
        }}
        onClick={(e) => {
          if (!editor.isEditable) return;
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      <div
        className="absolute -bottom-2 right-0 w-4 h-4 cursor-text z-10"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          focusAfter();
        }}
      />

      {selected && editor.isEditable && (
        <>
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -top-1.5 -left-1.5 cursor-nwse-resize z-20"
            onMouseDown={(e) => handleResize(e, "tl")}
          />
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -top-1.5 -right-1.5 cursor-nesw-resize z-20"
            onMouseDown={(e) => handleResize(e, "tr")}
          />
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -bottom-1.5 -left-1.5 cursor-nesw-resize z-20"
            onMouseDown={(e) => handleResize(e, "bl")}
          />
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -bottom-1.5 -right-1.5 cursor-nwse-resize z-20"
            onMouseDown={(e) => handleResize(e, "br")}
          />

          {/* Toolbar rendered inline — stays inside the editor scroll container */}
          <div
            data-image-toolbar-portal
            contentEditable={false}
            style={{
              position: "absolute",
              top: 8,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 30,
              whiteSpace: "nowrap",
            }}
          >
            <div className="flex items-center gap-0.5 bg-gray-900/80 dark:bg-gray-800/90 backdrop-blur-sm rounded-[6px] px-1 py-0.5 shadow-lg">
              <button
                onClick={() => handleSetAlignment("left")}
                className={`p-1.5 rounded-[4px] transition-colors ${currentAlign === "left" ? "bg-white/20" : "hover:bg-white/10"}`}
                title="Align left"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="w-3.5 h-3.5 text-white"
                >
                  <path
                    d="M2 3h12M2 6h8M2 9h12M2 12h8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button
                onClick={() => handleSetAlignment("center")}
                className={`p-1.5 rounded-[4px] transition-colors ${currentAlign === "center" ? "bg-white/20" : "hover:bg-white/10"}`}
                title="Align center"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="w-3.5 h-3.5 text-white"
                >
                  <path
                    d="M2 3h12M4 6h8M2 9h12M4 12h8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button
                onClick={() => handleSetAlignment("right")}
                className={`p-1.5 rounded-[4px] transition-colors ${currentAlign === "right" ? "bg-white/20" : "hover:bg-white/10"}`}
                title="Align right"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="w-3.5 h-3.5 text-white"
                >
                  <path
                    d="M2 3h12M6 6h8M2 9h12M6 12h8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <div className="w-px h-4 bg-white/30 mx-0.5" />
              <button
                onClick={() => setSize("100%")}
                className="p-1.5 rounded-[4px] hover:bg-white/10 transition-colors"
                title="Full width"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="w-3.5 h-3.5 text-white"
                >
                  <path
                    d="M2 4v8M14 4v8M4 8h8M4 6l-2 2 2 2M12 6l2 2-2 2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                onClick={() => setSize(null)}
                className="p-1.5 rounded-[4px] hover:bg-white/10 transition-colors"
                title="Original size"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="w-3.5 h-3.5 text-white"
                >
                  <rect
                    x="2"
                    y="2"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path d="M6 6h4v4H6z" fill="currentColor" opacity="0.5" />
                </svg>
              </button>
              <div className="w-px h-4 bg-white/30 mx-0.5" />
              <div className="relative">
                <button
                  onClick={handleMoreMenu}
                  className="p-1.5 rounded-[4px] hover:bg-white/10 transition-colors"
                  title="More options"
                >
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    className="w-3.5 h-3.5 text-white"
                  >
                    <circle cx="8" cy="3" r="1.5" fill="currentColor" />
                    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
                    <circle cx="8" cy="13" r="1.5" fill="currentColor" />
                  </svg>
                </button>
                {showContextMenu && (
                  <div
                    data-image-toolbar-portal
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      right: 0,
                      zIndex: 40,
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="w-48 bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px] py-1">
                      <button
                        onClick={handleDownload}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                      >
                        <DownloadIcon className="w-4 h-4 text-dark-gray dark:text-dark-gray-dk" />{" "}
                        Download
                      </button>
                      <button
                        onClick={handleDuplicate}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                      >
                        <DuplicateIcon className="w-4 h-4 text-dark-gray dark:text-dark-gray-dk" />{" "}
                        Duplicate
                      </button>
                      <div className="h-px bg-border-gray dark:bg-border-gray-dk mx-2 my-1" />
                      <button
                        onClick={handleDelete}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </NodeViewWrapper>
  );
};
